<template>
    <main-layout>
        <template #header>
            <main-header :title="'공지사항' | translate">
            </main-header>
        </template>
        <template #feature>
            <div class="d-flex">
                <v-btn to="/home/main/managertnbox/board/edit" elevation="0" color="#F9FCFF">
                    <v-icon color="#0C98FE">mdi-plus</v-icon>
                    게시물작성
                </v-btn>
            </div>
        </template>
        <template #toolbar>
            <main-toolbar>
                <search-multi-select-box
                    :items="countryOptions"
                    :label="'국가 선택' | translate"
                    @update:selected="onFilterChanged"
                    style="width: 125px"
                    v-model="searchOptions.countryCodeList"
                />
                <search-single-select-box
                    :items="noticeTypeOptions"
                    :label="'공지사항 종류' | translate"
                    @update:selected="onFilterChanged"
                    style="width: 0px"
                    v-model="searchOptions.boardTypeId"
                />
                <v-spacer></v-spacer>
                <search-bar
                    :placeholder="'제목/내용 검색어를 입력하세요.' | translate"
                    @update:keyword="onFilterChanged"
                    v-model="searchOptions.searchKeyword"
                    style="width: 450px;"
                />
            </main-toolbar>
        </template>

        <template #body>
            <main-body>
                <vue-data-table
                    ref="dataTable"
                    :fetch="fetch"
                    :headers="headers"
                    item-key="raw.boardId"
                    infinite-pagination
                    :items-per-page="30"
                >
                    <template #item.raw.title="{ item }">
                        <span class="short-text title-column" :title="item.raw.title" @click="onClickRow(item)">
                            {{ item.raw.title }}
                        </span>
                    </template>
                    <template #item.raw.regUserId="{ item }">
                        <div class="short-text regUserId-column" :title="item.raw.regUserId">{{ item.raw.regUserId }}</div>
                    </template>
                  <template #item.raw.noticeDetailYn="{ item }">
                    <div v-if="item.raw.noticeDetailYn === 'Y'">상세</div>
                    <div v-else>일반</div>
                  </template>
                    <template #item.raw.useYn="{ item }">
                        <div v-if="item.raw.useYn === 'Y'">게시 중</div>
                        <div v-else>게시 안함</div>
                    </template>
                    <template #item.raw.deleteYn="{ item }">
                        <div style="margin:0 auto;">
                            <v-icon @click="onDeleteClick(item.raw.boardId)">
                                mdi-delete-outline
                            </v-icon>
                        </div>
                    </template>
                </vue-data-table>
            </main-body>
        </template>
    </main-layout>
</template>

<script>
import {PATH} from "@/constants";
import MainBody from "@/components/common/layout/MainBody";
import MainHeader from "@/components/common/layout/MainHeader";
import MainLayout from "@/components/common/layout/MainLayout";
import MainToolbar from "@/components/common/layout/MainToolbar";
import TotalCodeOptions from "@/assets/options/TotalCodeOptions";
import Board from "@/model/board";
import VueDataTable from "@/assets/plugins/vue-data-table/VueDataTable";
import SearchSingleSelectBox from "@/components/input/SearchSingleSelectBox";
import SearchMultiSelectBox from "@/components/input/SearchMultiSelectBox";
import SearchBar from "@/components/input/SearchBar";
import {dateHelper} from "@/assets/plugins/mps-common/time/date-helper";

export default {
    name: "BoardList",
    components: {
        VueDataTable,
        MainLayout,
        MainHeader,
        MainBody,
        MainToolbar,
        SearchSingleSelectBox,
        SearchMultiSelectBox,
        SearchBar,
    },
    mixins: [TotalCodeOptions],
    data() {
        return {
            headers: [
                {
                    text: "ID",
                    value: "raw.boardId",
                    width: 60,
                },
                {
                  text: "국가",
                  value: "raw.countryCode",
                  width: 100,
                },
                {
                    text: "공지사항 종류",
                    value: "raw.boardTypeName",
                    width: 80,
                },
                {
                    text: "제목",
                    value: "raw.title",
                },
                {
                    text: "태그",
                    value: "raw.tags",
                    width: 220,
                },
                {
                    text: "작성자",
                    value: "raw.regUserId",
                    sortable: false,
                    width: 80,
                },
                {
                  text: "상세 여부",
                  value: "raw.noticeDetailYn",
                  sortable: false,
                  width: 80,
                },
                {
                    text: "게시 여부",
                    value: "raw.useYn",
                    sortable: false,
                    width: 80,
                },
                {
                    text: "삭제 처리",
                    value: "raw.deleteYn",
                    sortable: false,
                    width: 60,
                },
                {
                    text: "게시 시작일",
                    value: "raw.noticeStartDatetime",
                    read: (v, item) => dateHelper.formatAdminDate(v),
                },
                {
                    text: "게시 종료일",
                    value: "raw.noticeEndDatetime",
                    read: (v, item) => dateHelper.formatAdminDate(v),
                },
                {
                    text: "등록일자",
                    value: "raw.regDatetime",
                    read: (v, item) => dateHelper.formatAdminDate(v),
                },
                {
                    text: "수정일자",
                    value: "raw.uptDatetime",
                    read: (v, item) => dateHelper.formatAdminDate(v),
                },
            ],
            searchOptions: {
                searchKeyword: null,
                boardTypeId: null,
                countryCodeList: [],
            },
        }
    },
    created() {
        this.loadCountryOptions();
        this.loadNoticeTypeOptions();
    },
    methods: {
        async fetch(pageNumber, pageSize) {
            const res = await this.$request(PATH.BOARD.NOTICE.LIST)
                .setObject({
                    ...this.searchOptions,
                    pageNumber: pageNumber,
                    pageSize: pageSize,
                })
                .catch()
                .enqueue();
            return res.data.filter(d => d.boardTypeCd === 'N').map(e => new Board(e))
        },

        /**
         * 필터 조건이 변경되었습니다. 목록을 초기화하고 다시 불러옵니다.
         */
        onFilterChanged() {
            this.$refs.dataTable.changeType();
        },

        onClickRow(item) {
            this.$router.push({name: "managertnbox.board.edit", params: {raw: item.raw}})
        },

        async onDeleteClick(boardId) {
            try {
                if (!confirm(this.t('삭세하시겠습니까?'))) {
                    return;
                }
                await this.$request(PATH.BOARD.NOTICE.DELETE)
                    .setObject({ boardId: boardId })
                    .enqueue()
                alert('삭제 되었습니다.')
                // 삭제한 목록 Reload
                this.$router.go()
            } catch (error) {
                console.error(error)
            }
        },
    }
}
</script>

<style scoped>
>>> input[type=checkbox] {
    position: absolute;
}
.short-text{
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

}
.title-column{
    width: 300px;
    cursor: pointer;
    text-decoration: underline
}
.regUserId-column{
    width: 70px;
}
</style>
